export const SITE_URL = 'https://retreateast.co.uk';
export const SITE_NAME = 'Retreat East';
export const SITE_LOGO = `${SITE_URL}/images/logo.png`;

export const ORG_ID = `${SITE_URL}#organization`;
export const HOTEL_ID = `${SITE_URL}#hotel`;
export const RESTAURANT_ID = `${SITE_URL}/dine#restaurant`;
export const SPA_BUSINESS_ID = `${SITE_URL}/spa#business`;
export const WEBSITE_ID = `${SITE_URL}#website`;

// Cross-page @id references must include @type so validator.schema.org can
// verify the property's expected types without needing the referenced node
// to be present in the same JSON-LD document.
export const ORG_REF = { '@type': 'Organization', '@id': ORG_ID };
export const HOTEL_REF = { '@type': 'Hotel', '@id': HOTEL_ID };
export const RESTAURANT_REF = { '@type': 'Restaurant', '@id': RESTAURANT_ID };
export const SPA_BUSINESS_REF = {
    '@type': 'HealthAndBeautyBusiness',
    '@id': SPA_BUSINESS_ID,
};

export const ORGANIZATION_BASE = {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE_NAME,
    url: SITE_URL,
    logo: SITE_LOGO,
    image: SITE_LOGO,
    telephone: '+44 1473 943019',
    email: 'reception@retreateast.co.uk',
    address: {
        '@type': 'PostalAddress',
        streetAddress: 'Hemingstone Road',
        addressLocality: 'Coddenham',
        addressRegion: 'Suffolk',
        postalCode: 'IP6 9TH',
        addressCountry: 'GB',
    },
    sameAs: [
        'https://www.facebook.com/retreateast/',
        'https://www.instagram.com/retreat.east/',
    ],
};

export const organization = (extras = {}) => ({
    '@context': 'https://schema.org',
    ...ORGANIZATION_BASE,
    ...extras,
});

export const webSite = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: SITE_NAME,
    url: SITE_URL,
    publisher: ORG_REF,
});

export const webPage = ({ pathname, name, description }) => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}${pathname}#webpage`,
    url: `${SITE_URL}${pathname}`,
    ...(name && { name }),
    ...(description && { description }),
    isPartOf: { '@type': 'WebSite', '@id': WEBSITE_ID },
});

export const breadcrumbList = items => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url?.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
    })),
});

const BREADCRUMB_LABEL_OVERRIDES = {
    '': 'Home',
    'stay': 'Stay',
    'barns-and-rooms': 'Barns & Rooms',
    'spa': 'Spa',
    'treatments': 'Treatments',
    'dine': 'Dine',
    'menus': 'Menus',
    'weddings': 'Weddings',
    'faqs': 'FAQs',
    'whats-on': "What's On",
    'events': 'Events',
    'blog': 'Blog',
    'shop': 'Shop',
    'explore': 'Explore',
    'our-gin': 'Our Gin',
    'contact-us': 'Contact',
    'sustainability': 'Sustainability',
    'awards': 'Awards',
};

const labelForSegment = segment =>
    BREADCRUMB_LABEL_OVERRIDES[segment] ||
    segment
        .split('-')
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');

export const breadcrumbForPath = pathname => {
    const segments = (pathname || '/').split('/').filter(Boolean);
    const items = [{ name: 'Home', url: '/' }];
    let currentPath = '';
    segments.forEach(segment => {
        currentPath += `/${segment}`;
        items.push({ name: labelForSegment(segment), url: currentPath });
    });
    return breadcrumbList(items);
};

export const faqPage = faqs => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (faqs || []).map(({ question, answer }) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
            '@type': 'Answer',
            text: typeof answer === 'string' ? answer.replace(/<[^>]+>/g, '') : '',
        },
    })),
});

export const stripHtml = (value = '') =>
    typeof value === 'string' ? value.replace(/<[^>]+>/g, '').trim() : '';
